export type Nullable<T> = T | null;

export type Token = Nullable<string>;

export type TempReading = Nullable<number>;

export type TimeTempPair = [number, number];

export type JWTPayload = {
  username: string;
  user_id: number;
  email: string;
  exp: number;
};

// UI types
export type SelectChoice = {
  label: string;
  value: string;
  name?: string;
};

/* Versions that come back from the API */
export type ResourceVersions = {
  roastables: number;
  roasts: number;
  beans: number;
  roasters: number;
};

/* Beans */
export type Bean = {
  current_amount: string;
  id: number;
  ico_mark: string;
  is_active: boolean;
  location: number;
  lot: string;
  name: string;
  version: number;
};
export type Beans = Bean[];

/* Locations */
export type Location = {
  id: number;
  name: string;
  is_active: boolean;
  onsite: boolean;
};
export type Locations = Location[];

/* Thermocouples */
export enum TCType {
  K = 'K',
  J = 'J'
}
export type Thermocouple = {
  name: string;
  show: boolean;
  color: string;
  phidget?: {
    type?: number;
    channel?: number;
    hubPort?: number;
  };
  plotRoR?: boolean;
  offset: number;
  active: boolean;
  type: TCType;
  id: number;
};

/* Roasters */
export type TCNumberLabels = '1' | '2' | '3' | '4';
export type TCSettings = { [K in TCNumberLabels]: Thermocouple };
export type RoasterTCSettings = {
  thermocouples?: TCSettings;
  mass_scale?: MassScale;
};

export type Roaster = {
  id: string;
  is_active: boolean;
  location_id: number;
  name: string;
  capacity: string;
  capacity_scale: string;
  version: number;
  settings: RoasterTCSettings;
};
export type Roasters = Roaster[];
export type RoasterMapping = {
  [key: string]: Roaster;
};

/* Roastables */
export type Component = {
  bean_id: number;
  percent: number;
  // TODO
  // Bean name was added later, so some people will have cached versions without this. By Feb 20th
  // this is safe to take out and have it be non-optional since people will have had to re-login by
  // then. For now, make it optional for safety's sake.
  bean_name?: string;
};
export type Roastable = {
  id: number;
  components: Component[];
  is_active: boolean;
  is_blend: boolean;
  location: number;
  name: string;
  version: number;
};
export type Roastables = Roastable[];

//
// Roasting events that you create during a roast. These are displayed on the settings.
//
export type EventScale = {
  min: Nullable<string>;
  max: Nullable<string>;
  step: Nullable<string>;
};
export type RoastingEvent = {
  id?: number;
  name: string;
  type: string;
  color?: string;
  start_phase?: string;
  end_phase?: string;
  scale?: EventScale;
};
// End roasting events

/* Roasts */
export type NewRoastEvent = {
  id?: string; // UUID for new events so we can update them easily
  time: number;
  formattedTime?: string;
  temperature: string;
  description: string;
  isFirstCrack: boolean;
  color?: string;
};

export type RoastProfile = {
  events: NewRoastEvent[];
  id: string;
  power: number;
  charts: string;
};
export type Roast = {
  id: number;
  added_by: {
    username: string;
    fullname: string;
    id: number;
  };
  account: number;
  end_mass: number;
  humidity: string;
  location: number;
  name: string;
  notes: string;
  is_active: boolean;
  pressure: number;
  profile: RoastProfile;
  roast_id: number;
  starred: boolean;
};
export type Roasts = Roast[];

// New roast
type ProfileData = {
  temperatures?: string;
  label: string;
};
type NewRoastProfile = {
  data: ProfileData[];
  burner?: TimeTempPair[];
  events: NewRoastEvent[];
  times: number[];
  corrected_hr?: boolean;
};
export type NewRoast = {
  id: string;
  profile: NewRoastProfile;
  roaster: string;
  roastable: number;
  components: Component[];
  location: number;
  timestamp: number;
  pressure?: number;
  start_mass?: number;
  notes?: string;
  humidity?: number;
  device_type?: string;
};
// End new roast

export type SyncApiResonse = {
  beans: Beans;
  locations: Locations;
  roasts: Roasts;
  roasters: Roasters;
  roastables: Roastables;
  versions: ResourceVersions;
};

// Begin starred roasts

type AddedBy = {
  id: number;
  username: string;
  fullname: string;
};

type Costs = {
  roasted_cost_per_mass: number;
  total_green_cost: number;
  missing_costs: boolean;
  green_cost_per_mass: number;
};

export type StarredRoastEvent = {
  percentage: string;
  description: string;
  temperature: number;
  auto_event: boolean;
  time: string;
};

type RecipeItem = {
  bean_id: number;
  percent: number;
  name: string;
};

export type RoastChartData = [
  beanTemp: TimeTempPair[],
  tc2: Nullable<TimeTempPair[]>,
  tc3: Nullable<TimeTempPair[]>,
  tc4: Nullable<TimeTempPair[]>,
  heatingRate?: Nullable<TimeTempPair[]>
];

export type StarredRoastProfile = {
  id: string;
  burner: TimeTempPair[];
  labels: string[];
  events: StarredRoastEvent[];
  power: number;
  plots: RoastChartData;
};

export type StarredRoast = {
  added_by: AddedBy;
  end_mass: number;
  humidity: string;
  location: number;
  name: string;
  notes: string;
  is_active: boolean;
  pressure: number;
  costs: Costs;
  profile: StarredRoastProfile;
  public: boolean;
  roastable_definition_id: number;
  roaster: string;
  roast_id: number;
  starred: boolean;
  start_mass: number;
  timestamp: number;
  recipe: RecipeItem[];
  roast_level?: number;
};
export type StarredRoasts = StarredRoast[];

type RecipeComponent = {
  bean_id: number;
  percent: number;
  name: string;
};

export type NewRoastResponse = {
  id: number;
  added_by: AddedBy;
  account: number;
  end_mass: Nullable<number>;
  humidity: Nullable<number>;
  location: number;
  name: string;
  notes: string;
  is_active: boolean;
  pressure: number | null;
  profile: null;
  public: boolean;
  roastable: number;
  roastable_definition_id: number;
  roaster: string;
  roast_id: number;
  starred: boolean;
  start_mass: Nullable<number>;
  timestamp: number;
  version: number;
  components: [number, number][]; // Tuple array of [id, percentage]
  recipe: RecipeComponent[];
};
// end roasts

// Databridge types
export type DataBridgeData = number[];

export type DataBridgeReadings = {
  humidity: Nullable<number>;
  pressure: Nullable<number>;
  tc1: number;
  tc2: number;
  tc3: number;
  tc4: number;
};
// End databridge types

// Roast Queue
export enum RoastQueueStatus {
  Failed = 'Failed to upload',
  Queued = 'Queued',
  InProgress = 'In progress',
  Retrying = 'Retrying upload...',
  Saving = 'Saving...',
  Uploaded = 'Uploaded'
}
export type RoastQueueItem = {
  id: string;
  roastId?: number;
  roastable?: Roastable;
  referenceRoastId?: string;
  referenceRoastableName?: string;
  startingMass?: number;
  endingMass?: number;
  roastLevel?: number;
  endingMassDisabled: boolean;
  status: RoastQueueStatus;
  notes?: string;
  uploaded: boolean;
};
export type RoastQueue = RoastQueueItem[];

// end Roast Queue

// User profile
export type UserProfile = {
  account: Account;
  date_joined: Date;
  email: string;
  first_name: string;
  id: number;
  last_login: Date;
  last_name: string;
  location: Location;
  permissions: string[];
  profile: ProfileSettings;
  username: string;
};

export enum TemperatureScale {
  Celsius = 'C',
  Fahrenheit = 'F',
  CelsiusLower = 'c',
  FahrenheitLower = 'f'
}
export enum MassScale {
  Pounds = 'lb',
  Kilograms = 'kg',
  Grams = 'g'
}

type ProfileSettings = {
  mass_scale: MassScale;
  temperature_scale: TemperatureScale;
  pressure_scale: string;
  timezone: string;
  currency: string;
  cupping_form: string;
};

type Account = {
  id: number;
  users: AccountUser[];
  name: string;
  slug: string;
  created_on: Date;
  active: boolean;
  payment_due: Date;
  timezone: string;
  mass_scale: string;
  temperature_scale: string;
  use_negative_inventory: boolean;
  roasts_private: boolean;
  group: number;
  subscription: number;
};

type AccountUser = {
  username: string;
  id: number;
  full_name: string;
};
// end User profile
